import type { AlertCategory } from '../../services/admin/alertsApi'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  alertsApi,
  type AdminAlertConfig,
  type AdminAlertRecipientInput,
} from '../../services/admin/alertsApi'

const RECIPIENTS_KEY = ['admin', 'catch-all', 'recipients']
const CONFIG_KEY = ['admin', 'catch-all', 'config']
const MUTES_KEY = ['admin', 'catch-all', 'device-mutes']

const errMessage = (error: any, fallback: string) =>
  error?.response?.data?.error?.message || error?.message || fallback

// --- Recipients ---

export function useAdminAlertRecipients() {
  return useQuery({ queryKey: RECIPIENTS_KEY, queryFn: () => alertsApi.listRecipients() })
}

export function useCreateAdminAlertRecipient() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: AdminAlertRecipientInput) => alertsApi.createRecipient(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RECIPIENTS_KEY })
      toast.success('Recipient added')
    },
    onError: (error: any) => toast.error(errMessage(error, 'Failed to add recipient')),
  })
}

export function useUpdateAdminAlertRecipient() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: Partial<AdminAlertRecipientInput> }) =>
      alertsApi.updateRecipient(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RECIPIENTS_KEY })
      toast.success('Recipient updated')
    },
    onError: (error: any) => toast.error(errMessage(error, 'Failed to update recipient')),
  })
}

export function useDeleteAdminAlertRecipient() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => alertsApi.deleteRecipient(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RECIPIENTS_KEY })
      toast.success('Recipient removed')
    },
    onError: (error: any) => toast.error(errMessage(error, 'Failed to remove recipient')),
  })
}

// --- Config ---

export function useAdminAlertConfig() {
  return useQuery({ queryKey: CONFIG_KEY, queryFn: () => alertsApi.getConfig() })
}

export function useUpdateAdminAlertConfig() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: Partial<AdminAlertConfig>) => alertsApi.updateConfig(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CONFIG_KEY })
      toast.success('Settings saved')
    },
    onError: (error: any) => toast.error(errMessage(error, 'Failed to save settings')),
  })
}

// --- Device mutes ---

export function useAdminDeviceMutes() {
  return useQuery({ queryKey: MUTES_KEY, queryFn: () => alertsApi.listDeviceMutes() })
}

export function useSetDeviceMute() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ deviceId, muted, reason }: { deviceId: number; muted: boolean; reason?: string | null }) =>
      alertsApi.setDeviceMute(deviceId, muted, reason),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: MUTES_KEY }),
    onError: (error: any) => toast.error(errMessage(error, 'Failed to update mute')),
  })
}

export function useUnmuteAll() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => alertsApi.unmuteAll(),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: MUTES_KEY })
      toast.success(`Unmuted ${result.unmuted} device${result.unmuted === 1 ? '' : 's'}`)
    },
    onError: (error: any) => toast.error(errMessage(error, 'Failed to unmute all')),
  })
}

// --- Feature flags -----------------------------------------------------------

const FLAGS_KEY = ['admin', 'feature-flags']

export function useAdminFeatureFlags() {
  return useQuery({ queryKey: FLAGS_KEY, queryFn: () => alertsApi.listFeatureFlags() })
}

export function useSetFeatureFlag() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ feature, enabled }: { feature: string; enabled: boolean }) =>
      alertsApi.setFeatureFlag(feature, enabled),
    onSuccess: (_d, { enabled }) => {
      qc.invalidateQueries({ queryKey: FLAGS_KEY })
      // The processor re-reads flags every 30s, so say so rather than implying
      // the change is instantaneous.
      toast.success(`Alert phase ${enabled ? 'enabled' : 'disabled'} — takes effect within 30s`)
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.error?.message || e?.message || 'Could not change the flag'),
  })
}

// --- Per-recipient subscriptions ---------------------------------------------

const SUBS_KEY = ['admin', 'alert-subscriptions']

export function useAlertSubscriptions() {
  return useQuery({ queryKey: SUBS_KEY, queryFn: () => alertsApi.getSubscriptions() })
}

export function useSetAlertSubscription() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      recipientId,
      category,
      subscribed,
    }: {
      recipientId: number
      category: AlertCategory
      subscribed: boolean
    }) => alertsApi.setSubscription(recipientId, category, subscribed),
    onSuccess: () => qc.invalidateQueries({ queryKey: SUBS_KEY }),
    onError: (e: any) =>
      toast.error(e?.response?.data?.error?.message || e?.message || 'Could not update subscription'),
  })
}
